import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { BsCalculator } from 'react-icons/bs';
import { crvProductService, phProductService } from '../services/productService';
import { amortizationService } from '../services/amortizationService';
import { loyaltyService } from '../services/loyaltyService';
import { CrvProduct, PhProduct, PRODUCT_TYPE_LABELS } from '../types/InvestmentProduct';
import { AmortizationScheduleDTO } from '../types/AmortizationSchedule';
import { LoyaltyScheduleDTO } from '../types/LoyaltySchedule';
import { formatCurrency } from '../utils/format';
import './AmortizationSchedule.css';

type TabType = 'crv' | 'ph';
type DataTabType = 'amortization' | 'loyalty';

const AmortizationSchedule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('crv');
  const [activeDataTab, setActiveDataTab] = useState<DataTabType>('amortization');
  const [products, setProducts] = useState<(CrvProduct | PhProduct)[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [schedule, setSchedule] = useState<AmortizationScheduleDTO | null>(null);
  const [loyaltySchedule, setLoyaltySchedule] = useState<LoyaltyScheduleDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const phaseScrollRefs = useRef<(HTMLDivElement | null)[]>([]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    setSelectedProductId('');
    setSchedule(null);
    setLoyaltySchedule(null);
    try {
      if (activeTab === 'crv') {
        const response = await crvProductService.getAll();
        setProducts(response.data);
      } else {
        const response = await phProductService.getAll();
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, [activeTab]);

  const loadSchedule = useCallback(async () => {
    if (!selectedProductId) return;
    setLoading(true);
    try {
      if (activeTab === 'crv') {
        const response = await amortizationService.getCrvSchedule(selectedProductId);
        setSchedule(response.data);
      } else {
        const response = await amortizationService.getPhSchedule(selectedProductId);
        setSchedule(response.data);
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProductId, activeTab]);

  const loadLoyaltySchedule = useCallback(async () => {
    if (!selectedProductId) return;
    setLoading(true);
    try {
      if (activeTab === 'crv') {
        const response = await loyaltyService.getCrvLoyalty(selectedProductId);
        setLoyaltySchedule(response.data);
      } else {
        const response = await loyaltyService.getPhLoyalty(selectedProductId);
        setLoyaltySchedule(response.data);
      }
    } catch (error) {
      console.error('Error loading loyalty schedule:', error);
      setLoyaltySchedule(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProductId, activeTab]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (selectedProductId) {
      if (activeDataTab === 'amortization') {
        loadSchedule();
      } else {
        loadLoyaltySchedule();
      }
    } else {
      setSchedule(null);
      setLoyaltySchedule(null);
    }
  }, [selectedProductId, activeDataTab, loadSchedule, loadLoyaltySchedule]);

  // Sincronizar scroll horizontal entre todas las fases
  useEffect(() => {
    const wrappers = phaseScrollRefs.current.filter(Boolean);

    const handleScroll = (e: Event) => {
      const source = e.target as HTMLDivElement;
      const scrollLeft = source.scrollLeft;

      // Sincronizar todos los demás wrappers
      wrappers.forEach(wrapper => {
        if (wrapper && wrapper !== source) {
          wrapper.scrollLeft = scrollLeft;
        }
      });
    };

    wrappers.forEach(wrapper => {
      wrapper?.addEventListener('scroll', handleScroll);
    });

    return () => {
      wrappers.forEach(wrapper => {
        wrapper?.removeEventListener('scroll', handleScroll);
      });
    };
  }, [schedule, loyaltySchedule]);

  const formatPercent = (value?: number | null): string => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + '%';
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return '-';
    const date = new Date(value);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const exportAmortizationCsv = () => {
    if (!schedule) return;

    const separator = ';';
    const lines: string[] = [];

    // Title
    lines.push(`Cuadro de Amortización - ${schedule.productName} (${schedule.productType})`);
    lines.push('');

    for (const phase of schedule.phases) {
      // Phase header
      lines.push(`FASE: ${phase.phaseName} | Frecuencia: ${phase.frecuenciaPago}`);

      // Column headers
      const headers = [
        'N', 'Inversor', 'Importe', 'Rto.total%', 'Realizada trans.',
        'Rto.total €', 'Fecha max Rto.3%', 'Fecha inicio pago',
        ...phase.periodLabels
      ];
      lines.push(headers.join(separator));

      // Investor rows
      for (const inv of phase.investors) {
        const row = [
          inv.rowNumber,
          `"${inv.investorName}"`,
          formatNumberForCsv(inv.importe),
          formatNumberForCsv(inv.rtoTotalPercent),
          formatDateForCsv(inv.fechaTransRecibida),
          formatNumberForCsv(inv.rtoTotalEur),
          formatDateForCsv(inv.fechaMaxRto3),
          formatDateForCsv(inv.fechaInicioPago),
          ...inv.periodPayments.map(p => formatNumberForCsv(p))
        ];
        lines.push(row.join(separator));
      }

      // Subtotal
      const subtotalRow = [
        '', 'SUBTOTAL', formatNumberForCsv(phase.subtotal.totalImporte),
        '', '', formatNumberForCsv(phase.subtotal.totalRtoTotalEur),
        '', '',
        ...phase.subtotal.totalPeriodPayments.map(p => formatNumberForCsv(p))
      ];
      lines.push(subtotalRow.join(separator));
      lines.push('');
    }

    // Grand total
    if (schedule.grandTotal) {
      const grandRow = [
        '', 'GRAN TOTAL', formatNumberForCsv(schedule.grandTotal.totalImporte),
        '', '', formatNumberForCsv(schedule.grandTotal.totalRtoTotalEur),
        '', '',
        ...(schedule.grandTotal.totalPeriodPayments ?? []).map(p => formatNumberForCsv(p))
      ];
      lines.push(grandRow.join(separator));
    }

    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `amortizacion_${schedule.productId}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportLoyaltyCsv = () => {
    if (!loyaltySchedule) return;

    const separator = ';';
    const lines: string[] = [];

    // Title
    lines.push(`Cuadro de Fidelización - ${loyaltySchedule.productName} (${loyaltySchedule.productType})`);
    lines.push('');

    for (const phase of loyaltySchedule.phases) {
      // Phase header
      lines.push(`FASE: ${phase.phaseName} | Frecuencia: ${phase.frecuenciaPago}`);

      // Column headers
      const headers = [
        'N', 'Inversor', 'Importe', 'Fidelización%', 'Fecha transferencia',
        'Fidelización total €',
        ...phase.periodLabels
      ];
      lines.push(headers.join(separator));

      // Investor rows
      for (const inv of phase.investors) {
        const row = [
          inv.rowNumber,
          `"${inv.investorName}"`,
          formatNumberForCsv(inv.importe),
          formatNumberForCsv(inv.fidelizacionPercent),
          formatDateForCsv(inv.fechaInicioPago),
          formatNumberForCsv(inv.fidelizacionTotalEur),
          ...inv.periodPayments.map(p => formatNumberForCsv(p))
        ];
        lines.push(row.join(separator));
      }

      // Subtotal
      const subtotalRow = [
        '', 'SUBTOTAL', formatNumberForCsv(phase.subtotal.totalImporte),
        '', '', formatNumberForCsv(phase.subtotal.totalFidelizacionEur),
        ...phase.subtotal.totalPeriodPayments.map(p => formatNumberForCsv(p))
      ];
      lines.push(subtotalRow.join(separator));
      lines.push('');
    }

    // Grand total
    if (loyaltySchedule.grandTotal) {
      const grandRow = [
        '', 'GRAN TOTAL', formatNumberForCsv(loyaltySchedule.grandTotal.totalImporte),
        '', '', formatNumberForCsv(loyaltySchedule.grandTotal.totalFidelizacionEur),
        ...(loyaltySchedule.grandTotal.totalPeriodPayments ?? []).map(p => formatNumberForCsv(p))
      ];
      lines.push(grandRow.join(separator));
    }

    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fidelizacion_${loyaltySchedule.productId}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDateForCsv = (value?: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatNumberForCsv = (value?: number | null): string => {
    if (value === undefined || value === null) return '';
    return value.toFixed(2).replace('.', ',');
  };

  const fixedColumnHeaders = [
    'N', 'Inversor', 'Importe', 'Rto.total%', 'Realizada trans.',
    'Rto.total €', 'Fecha max Rto.3%', 'Fecha inicio pago'
  ];

  const loyaltyColumnHeaders = [
    'N', 'Inversor', 'Importe', 'Fidelización%', 'Fecha transferencia',
    'Fidelización total €'
  ];

  const renderTable = () => {
    if (!schedule || schedule.phases.length === 0) {
      return <EmptyState icon={BsCalculator} title="Sin datos de amortización" subtitle="No hay datos de amortización para este producto." />;
    }

    return (
      <>
        {schedule.phases.map((phase, index) => (
          <div key={phase.phaseId} className="phase-section">
            <div className="phase-table-wrapper" ref={el => phaseScrollRefs.current[index] = el}>
              <table className="amortization-table">
                <thead>
                  <tr className="phase-header-row">
                    <td colSpan={fixedColumnHeaders.length} className="phase-header-text">
                      {phase.phaseName} — {phase.frecuenciaPago}
                      {phase.fechaInicioFase && ` | Inicio: ${formatDate(phase.fechaInicioFase)}`}
                      {phase.fechaDevolucion && ` | Devolución: ${formatDate(phase.fechaDevolucion)}`}
                    </td>
                    {phase.periodLabels.map((_, i) => (
                      <td key={i}></td>
                    ))}
                  </tr>
                  <tr>
                    {fixedColumnHeaders.map((col, i) => (
                      <th key={i} className={
                        i === 0 ? 'col-num' :
                        i === 1 ? 'col-name' :
                        [2, 5].includes(i) ? 'col-currency' :
                        i === 3 ? 'col-percent' :
                        [4, 6, 7].includes(i) ? 'col-date' : ''
                      }>{col}</th>
                    ))}
                    {phase.periodLabels.map((label, i) => (
                      <th key={`pl-${i}`} className="col-period">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {phase.investors.map((inv) => (
                    <tr key={inv.sheetId} className="investor-row">
                      <td className="col-num">{inv.rowNumber}</td>
                      <td className="col-name">{inv.investorName}</td>
                      <td className="col-currency">{formatCurrency(inv.importe)}</td>
                      <td className="col-percent">{formatPercent(inv.rtoTotalPercent)}</td>
                      <td className="col-date">{formatDate(inv.fechaTransRecibida)}</td>
                      <td className="col-currency">{formatCurrency(inv.rtoTotalEur)}</td>
                      <td className="col-date">{formatDate(inv.fechaMaxRto3)}</td>
                      <td className="col-date">{formatDate(inv.fechaInicioPago)}</td>
                      {inv.periodPayments.map((p, i) => (
                        <td key={i} className="col-period">
                          {p > 0 ? formatCurrency(p) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Subtotal row */}
                  <tr className="subtotal-row">
                    <td className="col-num"></td>
                    <td className="col-name">SUBTOTAL</td>
                    <td className="col-currency">{formatCurrency(phase.subtotal.totalImporte)}</td>
                    <td className="col-percent"></td>
                    <td className="col-date"></td>
                    <td className="col-currency">{formatCurrency(phase.subtotal.totalRtoTotalEur)}</td>
                    <td className="col-date"></td>
                    <td className="col-date"></td>
                    {phase.subtotal.totalPeriodPayments.map((p, i) => (
                      <td key={i} className="col-period">
                        {p > 0 ? formatCurrency(p) : '-'}
                      </td>
                    ))}
                  </tr>
                </tbody>
            </table>
          </div>
        </div>
        ))}

        {/* Grand total */}
        {schedule.grandTotal && schedule.phases.length > 0 && (
          <div className="grand-total-section">
            <div className="phase-table-wrapper" ref={el => phaseScrollRefs.current[schedule.phases.length] = el}>
              <table className="amortization-table">
                <thead>
                  <tr>
                    {fixedColumnHeaders.map((col, i) => (
                      <th key={i} className={
                        i === 0 ? 'col-num' :
                        i === 1 ? 'col-name' :
                        [2, 5].includes(i) ? 'col-currency' :
                        i === 3 ? 'col-percent' :
                        [4, 6, 7].includes(i) ? 'col-date' : ''
                      }>{col}</th>
                    ))}
                    {Array.from({ length: Math.max(...schedule.phases.map(p => p.periodLabels.length)) }).map((_, i) => (
                      <th key={`pl-${i}`} className="col-period"></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="grand-total-row">
                    <td className="col-num"></td>
                    <td className="col-name">GRAN TOTAL</td>
                    <td className="col-currency">{formatCurrency(schedule.grandTotal.totalImporte)}</td>
                    <td className="col-percent"></td>
                    <td className="col-date"></td>
                    <td className="col-currency">{formatCurrency(schedule.grandTotal.totalRtoTotalEur)}</td>
                    <td className="col-date"></td>
                    <td className="col-date"></td>
                    {Array.from({ length: Math.max(...schedule.phases.map(p => p.periodLabels.length)) }).map((_, i) => {
                      const value = schedule.grandTotal.totalPeriodPayments?.[i];
                      return (
                        <td key={i} className="col-period">
                          {value && value > 0 ? formatCurrency(value) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderLoyaltyTable = () => {
    if (!loyaltySchedule || loyaltySchedule.phases.length === 0) {
      return <EmptyState icon={BsCalculator} title="Sin datos de fidelización" subtitle="No hay datos de fidelización para este producto." />;
    }

    return (
      <>
        {loyaltySchedule.phases.map((phase, index) => (
          <div key={phase.phaseId} className="phase-section">
            <div className="phase-table-wrapper" ref={el => phaseScrollRefs.current[index] = el}>
              <table className="amortization-table">
                <thead>
                  <tr className="phase-header-row">
                    <td colSpan={loyaltyColumnHeaders.length} className="phase-header-text">
                      {phase.phaseName} — {phase.frecuenciaPago}
                      {phase.fechaInicioFase && ` | Inicio fase: ${formatDate(phase.fechaInicioFase)}`}
                    </td>
                    {phase.periodLabels.map((_, i) => (
                      <td key={i}></td>
                    ))}
                  </tr>
                  <tr>
                    {loyaltyColumnHeaders.map((col, i) => (
                      <th key={i} className={
                        i === 0 ? 'col-num' :
                        i === 1 ? 'col-name' :
                        [2, 5].includes(i) ? 'col-currency' :
                        i === 3 ? 'col-percent' :
                        i === 4 ? 'col-date' : ''
                      }>{col}</th>
                    ))}
                    {phase.periodLabels.map((label, i) => (
                      <th key={`pl-${i}`} className="col-period">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {phase.investors.map((inv) => (
                    <tr key={inv.sheetId} className="investor-row">
                      <td className="col-num">{inv.rowNumber}</td>
                      <td className="col-name">{inv.investorName}</td>
                      <td className="col-currency">{formatCurrency(inv.importe)}</td>
                      <td className="col-percent">{formatPercent(inv.fidelizacionPercent)}</td>
                      <td className="col-date">{formatDate(inv.fechaInicioPago)}</td>
                      <td className="col-currency">{formatCurrency(inv.fidelizacionTotalEur)}</td>
                      {inv.periodPayments.map((p, i) => (
                        <td key={i} className="col-period">
                          {p > 0 ? formatCurrency(p) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Subtotal row */}
                  <tr className="subtotal-row">
                    <td className="col-num"></td>
                    <td className="col-name">SUBTOTAL</td>
                    <td className="col-currency">{formatCurrency(phase.subtotal.totalImporte)}</td>
                    <td className="col-percent"></td>
                    <td className="col-date"></td>
                    <td className="col-currency">{formatCurrency(phase.subtotal.totalFidelizacionEur)}</td>
                    {phase.subtotal.totalPeriodPayments.map((p, i) => (
                      <td key={i} className="col-period">
                        {p > 0 ? formatCurrency(p) : '-'}
                      </td>
                    ))}
                  </tr>
                </tbody>
            </table>
          </div>
        </div>
        ))}

        {/* Grand total */}
        {loyaltySchedule.grandTotal && loyaltySchedule.phases.length > 0 && (
          <div className="grand-total-section">
            <div className="phase-table-wrapper" ref={el => phaseScrollRefs.current[loyaltySchedule.phases.length] = el}>
              <table className="amortization-table">
                <thead>
                  <tr>
                    {loyaltyColumnHeaders.map((col, i) => (
                      <th key={i} className={
                        i === 0 ? 'col-num' :
                        i === 1 ? 'col-name' :
                        [2, 5].includes(i) ? 'col-currency' :
                        i === 3 ? 'col-percent' :
                        i === 4 ? 'col-date' : ''
                      }>{col}</th>
                    ))}
                    {Array.from({ length: Math.max(...loyaltySchedule.phases.map(p => p.periodLabels.length)) }).map((_, i) => (
                      <th key={`pl-${i}`} className="col-period"></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="grand-total-row">
                    <td className="col-num"></td>
                    <td className="col-name">GRAN TOTAL</td>
                    <td className="col-currency">{formatCurrency(loyaltySchedule.grandTotal.totalImporte)}</td>
                    <td className="col-percent"></td>
                    <td className="col-date"></td>
                    <td className="col-currency">{formatCurrency(loyaltySchedule.grandTotal.totalFidelizacionEur)}</td>
                    {Array.from({ length: Math.max(...loyaltySchedule.phases.map(p => p.periodLabels.length)) }).map((_, i) => {
                      const value = loyaltySchedule.grandTotal.totalPeriodPayments?.[i];
                      return (
                        <td key={i} className="col-period">
                          {value && value > 0 ? formatCurrency(value) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="amortization-page">
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'crv' ? 'active' : ''}`}
          onClick={() => setActiveTab('crv')}
        >
          {PRODUCT_TYPE_LABELS.crv}
        </button>
        <button
          className={`tab ${activeTab === 'ph' ? 'active' : ''}`}
          onClick={() => setActiveTab('ph')}
        >
          {PRODUCT_TYPE_LABELS.ph}
        </button>
        <div className="tab-actions">
          <div className="amortization-controls">
            <select
              className="product-selector"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              disabled={loadingProducts}
            >
              <option value="">Seleccionar producto...</option>
              {products.map((p) => (
                <option key={p.id} value={p.id!}>
                  {p.id} - {p.nombreProducto}
                </option>
              ))}
            </select>
            <button
              className="btn-icon-only btn-csv"
              onClick={activeDataTab === 'amortization' ? exportAmortizationCsv : exportLoyaltyCsv}
              disabled={activeDataTab === 'amortization' ? !schedule : !loyaltySchedule}
              title="Exportar CSV"
            >
              {React.createElement(FaFileCsv as React.ComponentType)}
            </button>
          </div>
        </div>
      </div>

      <div className="tabs data-tabs">
        <button
          className={`tab ${activeDataTab === 'amortization' ? 'active' : ''}`}
          onClick={() => setActiveDataTab('amortization')}
        >
          Amortización
        </button>
        <button
          className={`tab ${activeDataTab === 'loyalty' ? 'active' : ''}`}
          onClick={() => setActiveDataTab('loyalty')}
        >
          Fidelización
        </button>
      </div>

      <div className="amortization-content">
        {loading ? (
          <LoadingSpinner message={`Cargando cuadro de ${activeDataTab === 'amortization' ? 'amortización' : 'fidelización'}...`} />
        ) : !selectedProductId ? (
          <EmptyState icon={BsCalculator} title="Selecciona un producto" subtitle={`Elige un producto para ver el cuadro de ${activeDataTab === 'amortization' ? 'amortización' : 'fidelización'}.`} />
        ) : activeDataTab === 'amortization' ? (
          renderTable()
        ) : (
          renderLoyaltyTable()
        )}
      </div>
    </div>
  );
};

export default AmortizationSchedule;
